import { LazyQueryExecFunction, OperationVariables, useLazyQuery } from '@apollo/client';
import { Article } from 't2-graphql-types';
import { ArticleType } from 'src/components/Article/types';
import { getSortedPromptsQuery } from 'src/components/Prompt/queries/getSortedPromptsQuery';
import { TERRITORY_PROMPTS_SIZE } from 't2-keystone/src/schema/Article/constants';

type TTerritoryExploreAllSectionProps = {
  territoryId: string;
  initialPageSize?: number;
};

type TUseTerritoryPromptListReturnValue = {
  prompts: Article[];
  onLoadMore: () => void;
  isLoading: boolean;
  hasMorePromptsToLoad: boolean;
  promptsCount: number;
  getSortedPromptsByTerritoryId: LazyQueryExecFunction<any, OperationVariables>;
};

export const useTerritoryPromptList = ({
  territoryId,
  initialPageSize = TERRITORY_PROMPTS_SIZE,
}: TTerritoryExploreAllSectionProps): TUseTerritoryPromptListReturnValue => {
  const [getSortedPromptsByTerritoryId, { data, loading: isLoading, fetchMore }] = useLazyQuery(getSortedPromptsQuery, {
    notifyOnNetworkStatusChange: true,
    variables: {
      skip: 0,
      take: initialPageSize,
      where: { type: { equals: ArticleType.Prompt }, territory: { id: { equals: territoryId } } },
      infiniteScroll: true,
    },
  });
  const prompts = data?.getSortedArticles.articles;

  const hasMorePromptsToLoad = prompts?.length !== data?.getSortedArticles.articlesCount;

  const fetchMorePrompts = async () => {
    if (!hasMorePromptsToLoad || isLoading || !prompts?.length) return;

    await fetchMore({
      variables: {
        skip: prompts?.length,
        take: TERRITORY_PROMPTS_SIZE,
      },
    });
  };

  return {
    prompts: prompts || [],
    onLoadMore: fetchMorePrompts,
    isLoading,
    promptsCount: data?.getSortedArticles.articlesCount || 0,
    hasMorePromptsToLoad,
    getSortedPromptsByTerritoryId,
  };
};
